<template>
  <div class="health-data-tracker">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to access your personal health data tracking and get personalized health insights.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else class="row">
      <div class="col-lg-8">
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-activity me-2 text-success"></i>Health Data Recording
            </h5>
          </div>
          <div class="card-body">
            <!-- Data Input Form -->
            <div class="row mb-4">
              <div class="col-md-4">
                <label class="form-label">Weight (kg)</label>
                <div class="input-group">
                  <input type="number" class="form-control" v-model="todayData.weight" step="0.1" />
                  <span class="input-group-text">kg</span>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">Sleep Hours</label>
                <div class="input-group">
                  <input type="number" class="form-control" v-model="todayData.sleep" step="0.5" />
                  <span class="input-group-text">h</span>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">Mood Rating</label>
                <select class="form-select" v-model="todayData.mood">
                  <option value="">Select Mood</option>
                  <option value="5">Excellent</option>
                  <option value="4">Good</option>
                  <option value="3">Average</option>
                  <option value="2">Poor</option>
                  <option value="1">Very Poor</option>
                </select>
              </div>
            </div>

            <button class="btn btn-primary mb-4" @click="saveHealthData">
              <i class="bi bi-check me-2"></i>Save Today's Data
            </button>

            <!-- Charts -->
            <div class="charts-container">
              <div class="row">
                <div class="col-md-6 mb-4">
                  <div class="chart-card">
                    <h6 class="text-center mb-3">Weight Trend</h6>
                    <div class="simple-chart">
                      <div
                        v-for="(data, index) in weightChart"
                        :key="index"
                        class="chart-bar"
                        :style="{ height: getChartHeight(data.weight, 45, 80) + '%' }"
                        :title="`${data.date}: ${data.weight}kg`"
                      ></div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-4">
                  <div class="chart-card">
                    <h6 class="text-center mb-3">Sleep Quality</h6>
                    <div class="simple-chart">
                      <div
                        v-for="(data, index) in sleepChart"
                        :key="index"
                        class="chart-bar sleep-bar"
                        :style="{ height: getChartHeight(data.sleep, 0, 12) + '%' }"
                        :title="`${data.date}: ${data.sleep} hours`"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <!-- Health Summary -->
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-heart-pulse me-2"></i>Health Overview</h6>
          </div>
          <div class="card-body">
            <div class="health-metric mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span>Current Weight</span>
                <span class="fw-bold">{{ currentWeight }} kg</span>
              </div>
              <div class="text-small text-muted">Target: {{ targetWeight }} kg</div>
            </div>
            <div class="health-metric mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span>Average Sleep</span>
                <span class="fw-bold">{{ averageSleep }} hours</span>
              </div>
              <div class="text-small text-muted">Recommended: 7-9 hours</div>
            </div>
            <div class="health-metric">
              <div class="d-flex justify-content-between align-items-center">
                <span>Mood Index</span>
                <span class="fw-bold">{{ averageMood }}/5</span>
              </div>
              <div class="text-small text-muted">Weekly Average</div>
            </div>
          </div>
        </div>

        <!-- Quick Tips -->
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-lightbulb me-2"></i>Health Tips</h6>
          </div>
          <div class="card-body">
            <div class="tip-item">
              <p class="small mb-2">
                Remember to drink 8 glasses of water daily to maintain hydration
              </p>
            </div>
            <div class="tip-item">
              <p class="small mb-2">
                At least 30 minutes of aerobic exercise daily helps mental health
              </p>
            </div>
            <div class="tip-item">
              <p class="small mb-0">Maintain regular sleep schedule to help hormonal balance</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'

export default {
  name: 'HealthDataTracker',
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    return { user, isAuthenticated, initAuth }
  },
  data() {
    return {
      todayData: {
        weight: '',
        sleep: '',
        mood: '',
      },
      currentWeight: 58.5,
      targetWeight: 55.0,
      averageSleep: 7.2,
      averageMood: 4.1,
      weightChart: [],
      sleepChart: [],
    }
  },

  mounted() {
    this.initAuth()
    if (this.isAuthenticated) {
      this.loadHealthData()
      this.generateChartData()
    }
  },

  methods: {
    saveHealthData() {
      const healthData = {
        date: new Date().toISOString().split('T')[0],
        weight: this.todayData.weight,
        sleep: this.todayData.sleep,
        mood: this.todayData.mood,
      }

      let storedData = JSON.parse(localStorage.getItem('healthData') || '[]')

      const todayIndex = storedData.findIndex((item) => item.date === healthData.date)
      if (todayIndex > -1) {
        storedData[todayIndex] = healthData
      } else {
        storedData.push(healthData)
      }

      localStorage.setItem('healthData', JSON.stringify(storedData))

      alert('Health data saved successfully!')
      this.loadHealthData()
    },

    loadHealthData() {
      const storedData = JSON.parse(localStorage.getItem('healthData') || '[]')

      const today = new Date().toISOString().split('T')[0]
      const todayData = storedData.find((item) => item.date === today)

      if (todayData) {
        this.todayData = { ...todayData }
      }

      if (storedData.length > 0) {
        const weights = storedData
          .filter((item) => item.weight)
          .map((item) => parseFloat(item.weight))
        const sleeps = storedData.filter((item) => item.sleep).map((item) => parseFloat(item.sleep))
        const moods = storedData.filter((item) => item.mood).map((item) => parseFloat(item.mood))

        if (weights.length > 0) {
          this.currentWeight = weights[weights.length - 1]
        }
        if (sleeps.length > 0) {
          this.averageSleep = (sleeps.reduce((a, b) => a + b, 0) / sleeps.length).toFixed(1)
        }
        if (moods.length > 0) {
          this.averageMood = (moods.reduce((a, b) => a + b, 0) / moods.length).toFixed(1)
        }
      }
    },

    generateChartData() {
      const storedData = JSON.parse(localStorage.getItem('healthData') || '[]')

      const last7Days = []
      for (let i = 6; i >= 0; i--) {
        const date = new Date()
        date.setDate(date.getDate() - i)
        const dateStr = date.toISOString().split('T')[0]

        const dayData = storedData.find((item) => item.date === dateStr)
        last7Days.push({
          date: dateStr,
          weight: dayData?.weight || 58 + Math.random() * 4,
          sleep: dayData?.sleep || 6 + Math.random() * 4,
        })
      }

      this.weightChart = last7Days
      this.sleepChart = last7Days
    },

    getChartHeight(value, min, max) {
      return ((value - min) / (max - min)) * 100
    },
  },
}
</script>

<style scoped>
.health-metric {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f4;
}

.health-metric:last-child {
  border-bottom: none;
}

.text-small {
  font-size: 0.85rem;
}

.tip-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.tip-item:last-child {
  border-bottom: none;
}

/* Chart Styles */
.simple-chart {
  display: flex;
  align-items: end;
  justify-content: space-between;
  height: 120px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
}

.chart-bar {
  background: var(--bs-primary);
  border-radius: 4px 4px 0 0;
  width: calc(100% / 8);
  margin: 0 1px;
  min-height: 10px;
  transition: all 0.3s ease;
}

.chart-bar:hover {
  opacity: 0.8;
}

.sleep-bar {
  background: #28a745;
}

.chart-card {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

@media (max-width: 768px) {
  .chart-bar {
    width: calc(100% / 7 - 2px);
  }

  .row.mb-4 {
    margin-bottom: 1rem !important;
  }

  .row.mb-4 .col-md-4 {
    margin-bottom: 1rem;
  }
}
</style>
